import type IdentityProviderView from '@UI/Store/Sso/Views/IdentityProviderView';
import { css } from '@emotion/react';
import { Copy } from 'lucide-react';
import * as React from 'react';
import { useToast } from '@verclam/toast-v2';
import { Button, InputText, Message } from '@verclam/ui-kit';

const callbackRowStyle = css`
	display: flex;
	align-items: center;
	gap: 0.75rem;
`;

const buttonStyle = css`
	margin-top: .5rem;
`;

const callbackFieldStyle = css`
	flex: 1 1 auto;
`;

type Props = {
	readonly identityProvider: IdentityProviderView | null;
};

/**
 * Hands the administrator the redirect URI to register with the identity provider, and knows how to
 * copy it. Renders the waiting message instead when no configuration has been saved yet.
 */
const CallbackUrlField: React.FC<Props> = ({ identityProvider }) => {
	const toast = useToast();

	const handleCopyCallbackUrl = React.useCallback(() => {
		if (identityProvider === null) {
			return;
		}

		navigator.clipboard
			.writeText(identityProvider.callbackUrl)
			.then(() => {
				toast.current?.show({
					severity: 'success',
					summary: 'URI copiée',
					detail: 'L’URI de redirection a été copiée dans le presse-papiers.',
					life: 3000,
				});
			})
			.catch(() => {
				toast.current?.show({
					severity: 'error',
					summary: 'Copie impossible',
					detail: 'L’URI de redirection n’a pas pu être copiée automatiquement.',
					life: 4000,
				});
			});
	}, [identityProvider, toast]);

	if (identityProvider === null) {
		return (
			<Message
				severity="info"
				text="L’URI de redirection à enregistrer chez votre fournisseur sera affichée ici une fois cette première configuration enregistrée."
			/>
		);
	}

	return (
		<div css={callbackRowStyle}>
			<div css={callbackFieldStyle}>
				<InputText
					name="callbackUrl"
					label="URI de redirection à enregistrer chez le fournisseur"
					value={identityProvider.callbackUrl}
					readOnly
				/>
			</div>
			<Button
				css={buttonStyle}
				type="button"
				text
				label="Copier"
				icon={<Copy size={16} />}
				onClick={handleCopyCallbackUrl}
			/>
		</div>
	);
};

export default CallbackUrlField;
